import React, { useMemo, useState } from 'react';
import { Pencil, Undo2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApprovalPreview from '@/components/dashboard/ApprovalPreview';

/* Edit-then-approve (migration 072). The card's preview is also its editor:
 * J.O. clicks Edit, changes the words in place, and the preview redraws as
 * he types. Approve then sends HIS version; the approval lands as
 * 'modified' and Milli is handed the before and after.
 *
 * Which fields are editable depends on what is behind the card:
 *   social  → caption
 *   email   → subject, body
 *   article → title, body (markdown), cta_url
 *   content → facebook, instagram (the exact text each channel publishes)
 * The length rules the drafting tools enforce are checked here first so
 * the server never has to say no. (The dash and exclamation-point rule was
 * removed 2026-10-20, migration 112.) */

export const EDITABLE_FIELDS = {
  social: ['caption'],
  email: ['subject', 'body'],
  article: ['title', 'body', 'ctaUrl'],
  content: ['facebook', 'instagram'],
};

const SERVER_KEY = { ctaUrl: 'cta_url' };

/** The preview object with J.O.'s edits laid over Milli's draft. */
export function mergePreview(preview, edits) {
  if (!preview || !edits) return preview;
  const out = { ...preview };
  for (const [k, v] of Object.entries(edits)) if (typeof v === 'string') out[k] = v;
  return out;
}

/** Only the fields that actually differ, in the server's names. */
export function changedEdits(preview, edits) {
  if (!preview || !edits) return null;
  const out = {};
  for (const f of EDITABLE_FIELDS[preview.kind] || []) {
    if (typeof edits[f] === 'string' && edits[f] !== (preview[f] ?? '')) out[SERVER_KEY[f] || f] = edits[f];
  }
  return Object.keys(out).length ? out : null;
}

/** A plain-language problem with the edits, or null. */
export function validateEdits(preview, edits, t) {
  if (!preview || !edits) return null;
  const caption = edits.caption ?? preview.caption ?? '';
  if (preview.kind === 'social') {
    if (!caption.trim()) return t('approvals.editor.captionRequired');
    if (caption.length > 2200) return t('approvals.editor.captionLong');
  }
  if (preview.kind === 'content') {
    for (const f of ['facebook', 'instagram']) {
      const v = edits[f] ?? preview[f] ?? '';
      if (!v.trim()) return t('approvals.editor.captionRequired');
      if (v.length > 2200) return t('approvals.editor.captionLong');
    }
  }
  if (preview.kind === 'email') {
    if (!(edits.subject ?? preview.subject ?? '').trim()) return t('approvals.editor.subjectRequired');
    if ((edits.subject ?? '').length > 200) return t('approvals.editor.subjectLong');
    if (!(edits.body ?? preview.body ?? '').trim()) return t('approvals.editor.bodyRequired');
  }
  if (preview.kind === 'article') {
    if (!(edits.title ?? preview.title ?? '').trim()) return t('approvals.editor.titleRequired');
    if (!(edits.body ?? preview.body ?? '').trim()) return t('approvals.editor.bodyRequired');
    const cta = edits.ctaUrl ?? preview.ctaUrl ?? '';
    if (cta && !/^https?:\/\//.test(cta)) return t('approvals.editor.ctaUrl');
  }
  if (preview.kind === 'content' && !(edits.body ?? preview.body ?? '').trim()) return t('approvals.editor.bodyRequired');
  return null;
}

function Field({ id, label, value, onChange, rows, mono }) {
  const cls =
    'mt-1 w-full rounded-md border border-border bg-background text-foreground text-sm px-3 py-2 ' +
    'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40' + (mono ? ' font-mono text-[13px]' : '');
  return (
    <label className="block text-sm" htmlFor={id}>
      <span className="uppercase tracking-[0.14em] text-[11px] text-muted-foreground">{label}</span>
      {rows ? (
        <textarea id={id} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      ) : (
        <input id={id} type="text" value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </label>
  );
}

export default function ApprovalEditor({ preview, edits, onChange, t }) {
  const [editing, setEditing] = useState(false);
  const fields = (preview && EDITABLE_FIELDS[preview.kind]) || [];
  const merged = useMemo(() => mergePreview(preview, edits), [preview, edits]);
  const changed = useMemo(() => changedEdits(preview, edits), [preview, edits]);
  const problem = useMemo(() => validateEdits(preview, edits, t), [preview, edits, t]);

  if (!preview) return null;

  const set = (f) => (v) => onChange({ ...(edits || {}), [f]: v });
  const value = (f) => (edits && typeof edits[f] === 'string' ? edits[f] : preview[f] ?? '');
  const labels = {
    caption: t('approvals.editor.caption'),
    subject: t('approvals.editor.subject'),
    body: t('approvals.editor.body'),
    title: t('approvals.editor.title'),
    ctaUrl: t('approvals.editor.ctaUrl'),
    facebook: t('approvals.editor.facebook'),
    instagram: t('approvals.editor.instagram'),
  };
  const rows = { caption: 8, body: preview.kind === 'article' ? 18 : 10 };

  return (
    <div data-testid="approval-editor">
      <ApprovalPreview preview={merged} t={t} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {fields.length ? (
          <Button
            type="button"
            size="sm"
            variant={editing ? 'default' : 'outline'}
            onClick={() => setEditing((e) => !e)}
            data-testid="approval-edit-toggle"
          >
            {editing ? <Check size={14} className="mr-1.5" /> : <Pencil size={14} className="mr-1.5" />}
            {editing ? t('approvals.editor.done') : t('approvals.editor.edit')}
          </Button>
        ) : null}
        {changed ? (
          <>
            <span className="text-xs rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-600 border border-amber-500/30">
              {t('approvals.editor.edited')}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange(null)}
              className="text-muted-foreground"
              data-testid="approval-edit-undo"
            >
              <Undo2 size={14} className="mr-1.5" />
              {t('approvals.editor.undo')}
            </Button>
          </>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3">
          {fields.map((f) => (
            <Field
              key={f}
              id={`edit-${preview.kind}-${f}`}
              label={labels[f]}
              value={value(f)}
              onChange={set(f)}
              rows={rows[f]}
              mono={f === 'body' && preview.kind === 'article'}
            />
          ))}
          <p className="text-[12px] text-muted-foreground">
            {preview.kind === 'social' || preview.kind === 'content' ? t('approvals.editor.rulesSocial') : t('approvals.editor.rulesGeneral')}
          </p>
        </div>
      ) : null}
      {problem ? (
        <p className="mt-2 text-sm text-red-600" data-testid="approval-edit-problem">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
